import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useReducedMotion } from "../../lib/useReducedMotion.js";
import ScanScene from "../ScanScene/ScanScene.jsx";
import CodeChip from "../CodeChip/CodeChip.jsx";
import styles from "./ScanStory.module.css";

/**
 * Scroll progress through the story (0-1) to the step on show. Each step owns one screen
 * of scroll, so the switch to step k falls halfway between screens: (k - 0.5) / (n - 1).
 */
const phaseOf = (p, n) => Math.min(n - 1, Math.floor(p * (n - 1) + 0.5));

/**
 * A story told in steps over a live scan: the scan stays pinned while the steps scroll
 * over it, and the page's scroll position (not a timer) drives it.
 *
 * `Scene` draws the scan (ScanScene by default) and gets `progressRef`, `phase` (the step
 * on show), `final`, `textEdge` (where the step text ends, px from the left, for framing
 * beside it) and `scene`'s props. For ScanScene, `scene` holds the story numbers
 * (from, travel, analyseAt, classifyAt), which should line up with phaseOf's switches.
 *
 * A step has `title` and `text`, and optionally `result` (a "What you get" line), `code`
 * and `tint` (a chip naming it), and `legend` ([{ label, color: [r, g, b] }]) for colours it
 * introduces. `action` and `note` close the last step. `noun` names the steps ("Step 2 of
 * 4"). Under reduced motion the story is a still beside a plain list, unless
 * `reducedLayout="pinned"`: then it stays pinned and the scene cuts between stills.
 */
export default function ScanStory({
  titleId,
  kicker,
  title,
  steps,
  Scene = ScanScene,
  scene,
  action,
  note,
  noun = "Step",
  reducedLayout = "static",
  className = "",
}) {
  const root = useRef(null);
  const firstCard = useRef(null);
  const progress = useRef(0);
  const [phase, setPhase] = useState(0);
  const [textEdge, setTextEdge] = useState(0);
  const reduced = useReducedMotion();
  const isStatic = reduced && reducedLayout === "static";
  const last = steps.length - 1;

  useEffect(() => {
    const el = root.current;
    const card = firstCard.current;
    if (!el || !card || typeof ResizeObserver === "undefined") return undefined;
    // Every step's card sits in the same column, so the first one says where the text ends.
    const ro = new ResizeObserver(() => {
      setTextEdge(Math.round(card.getBoundingClientRect().right - el.getBoundingClientRect().left));
    });
    ro.observe(el);
    ro.observe(card);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (isStatic) return undefined;
    let raf = 0;
    const measure = () => {
      raf = 0;
      const el = root.current;
      if (!el) return;
      const box = el.getBoundingClientRect();
      const span = box.height - window.innerHeight;
      const p = span > 0 ? Math.min(1, Math.max(0, -box.top / span)) : 0;
      progress.current = p;
      setPhase(phaseOf(p, steps.length));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    raf = requestAnimationFrame(measure);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [isStatic, steps.length]);

  const shown = isStatic ? last : phase;
  return (
    <>
      {/* Phones only: the heading leads the story, so the first card leaves the scene in
          view like the others. Wider screens show it in the first card; only one of the
          two is ever displayed, and the section is labelled by the card's. */}
      <div className={styles.lead}>
        <p className={styles.kicker}>{kicker}</p>
        <h2 className={styles.title}>{title}</h2>
      </div>
      <div
        ref={root}
        className={`${styles.story} ${className}`.trim()}
        data-static={isStatic ? "" : undefined}
      >
        <div className={styles.stage}>
          <Scene
            progressRef={progress}
            phase={shown}
            final={shown === last}
            textEdge={textEdge}
            {...scene}
          />
          <div className={styles.scrim} aria-hidden="true" />
        </div>

        <ol className={styles.steps}>
          {steps.map((step, i) => (
            <li
              key={step.key || step.title}
              className={styles.step}
              data-active={i === shown ? "" : undefined}
            >
              <div ref={i === 0 ? firstCard : undefined} className={styles.card}>
                {i === 0 ? (
                  <>
                    <p className={styles.kicker}>{kicker}</p>
                    <h2 id={titleId} className={styles.title}>
                      {title}
                    </h2>
                  </>
                ) : null}
                <p className={styles.stepNo}>
                  {step.code ? <CodeChip code={step.code} tint={step.tint} /> : null}
                  {noun} {i + 1} of {steps.length}
                </p>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepText}>{step.text}</p>
                {step.result ? (
                  <p className={styles.stepResult}>
                    <span>What you get</span>
                    {step.result}
                  </p>
                ) : null}
                {step.legend ? (
                  // Explains the colours of a decorative scene, so it is hidden with it.
                  <ul className={styles.legend} aria-hidden="true">
                    {step.legend.map(({ label, color }) => (
                      <li key={label} style={{ "--swatch": `rgb(${color.join(", ")})` }}>
                        {label}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {i === last && action ? (
                  <p className={styles.actions}>
                    <Link to={action.to} className="btn btn-primary">
                      {action.label}
                    </Link>
                  </p>
                ) : null}
                {i === last && note ? <p className={styles.note}>{note}</p> : null}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </>
  );
}
